import React, { useState } from 'react';

import { CM_PER_INCH, cmToInches } from './calculatorUtils';
import css from './calculatorStyles.module.css';

// Valid height range: 5'5" (65") to 6'2" (74")
const MIN_HEIGHT_INCHES = 65;
const MAX_HEIGHT_INCHES = 74;

/**
 * UPT Vector Harness Sizing Calculator
 *
 * Based on information from Mark at UPT, shared at the
 * European Skydiving Symposium 2026.
 *
 * MLW = (height in inches) - (inseam without shoes in inches) - 20
 * Yoke: chest 38-42" => +1, 42-46" => +2, >46" => +3
 * Valid for heights between 5'5" and 6'2".
 */

function computeYokeAdjustment(chestInches) {
  if (chestInches < 38) return { val: 'Std', note: 'Chest under 38"' };
  if (chestInches <= 42) return { val: '+1', note: 'Chest 38-42"' };
  if (chestInches <= 46) return { val: '+2', note: 'Chest 42-46"' };
  return { val: '+3', note: 'Chest above 46"' };
}

function computeMLW(heightInches, inseamInches) {
  const mlw = heightInches - inseamInches - 20;
  // Round to nearest 0.5
  const rounded = Math.round(mlw * 2) / 2;
  return rounded;
}

const UPTCalculator = () => {
  const [unit, setUnit] = useState('cm');
  const [height, setHeight] = useState('');
  const [inseam, setInseam] = useState('');
  const [chest, setChest] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const handleCalculate = () => {
    setError(null);
    setResults(null);

    const h = parseFloat(height);
    const i = parseFloat(inseam);
    const c = parseFloat(chest);

    if (isNaN(h) || isNaN(i) || isNaN(c)) {
      setError('Please fill in all fields with valid numbers.');
      return;
    }

    // Convert to inches if in cm
    const hInches = unit === 'cm' ? cmToInches(h) : h;
    const iInches = unit === 'cm' ? cmToInches(i) : i;
    const cInches = unit === 'cm' ? cmToInches(c) : c;

    // Validate height range
    if (hInches < MIN_HEIGHT_INCHES || hInches > MAX_HEIGHT_INCHES) {
      const minCm = Math.round(MIN_HEIGHT_INCHES * CM_PER_INCH);
      const maxCm = Math.round(MAX_HEIGHT_INCHES * CM_PER_INCH);
      setError(
        unit === 'cm'
          ? `This calculator is valid for heights between ${minCm} cm (5'5") and ${maxCm} cm (6'2"). For sizes outside this range, contact UPT directly at markk@uptvector.com.`
          : `This calculator is valid for heights between 5'5" (65") and 6'2" (74"). For sizes outside this range, contact UPT directly at markk@uptvector.com.`
      );
      return;
    }

    // Validate inseam is less than height
    if (iInches >= hInches) {
      setError('Inseam must be less than height. Please check your measurements.');
      return;
    }

    const mlw = computeMLW(hInches, iInches);
    const yoke = computeYokeAdjustment(cInches);

    setResults({
      mlw: {
        val: mlw.toFixed(1).replace('.0', ''),
        note: `(${hInches.toFixed(1)}" - ${iInches.toFixed(1)}" - 20) = ${(hInches - iInches - 20).toFixed(1)}", rounded to ${mlw.toFixed(1)}"`,
      },
      yoke,
    });
  };

  const handleReset = () => {
    setHeight('');
    setInseam('');
    setChest('');
    setResults(null);
    setError(null);
  };

  return (
    <div className={css.calculator}>
      <section className={css.card}>
        <h4 className={css.sectionTitle}>Your Measurements</h4>
        <div className={css.unitToggle}>
          <button
            type="button"
            className={unit === 'cm' ? css.unitActive : css.unitBtn}
            onClick={() => setUnit('cm')}
          >
            cm
          </button>
          <button
            type="button"
            className={unit === 'in' ? css.unitActive : css.unitBtn}
            onClick={() => setUnit('in')}
          >
            inches
          </button>
        </div>
        <div className={css.grid}>
          <div className={css.field}>
            <label className={css.label} htmlFor="upt-height">
              Height ({unit})
            </label>
            <input
              id="upt-height"
              className={css.input}
              type="number"
              step="0.1"
              placeholder={unit === 'cm' ? 'e.g., 178' : 'e.g., 70'}
              value={height}
              onChange={e => setHeight(e.target.value)}
            />
          </div>
          <div className={css.field}>
            <label className={css.label} htmlFor="upt-chest">
              Chest Measurement ({unit})
            </label>
            <input
              id="upt-chest"
              className={css.input}
              type="number"
              step="0.1"
              placeholder={unit === 'cm' ? 'e.g., 102' : 'e.g., 40'}
              value={chest}
              onChange={e => setChest(e.target.value)}
            />
          </div>
          <div className={css.field}>
            <label className={css.label} htmlFor="upt-inseam">
              Inseam without shoes ({unit})
            </label>
            <input
              id="upt-inseam"
              className={css.input}
              type="number"
              step="0.1"
              placeholder={unit === 'cm' ? 'e.g., 82' : 'e.g., 32'}
              value={inseam}
              onChange={e => setInseam(e.target.value)}
            />
          </div>
        </div>
        <div className={css.actions}>
          <button type="button" className={css.btnPrimary} onClick={handleCalculate}>
            Calculate
          </button>
          <button type="button" className={css.btnSecondary} onClick={handleReset}>
            Reset
          </button>
        </div>

        {results && (
          <div className={css.results}>
            <div className={css.tile}>
              <h4 className={css.tileTitle}>Main Lift Web (MLW)</h4>
              <div className={css.tileRight}>
                <div className={css.tileValue}>{results.mlw.val}</div>
                <div className={css.tileMeta}>{results.mlw.note}</div>
              </div>
            </div>
            <div className={css.tile}>
              <h4 className={css.tileTitle}>Yoke Adjustment</h4>
              <div className={css.tileRight}>
                <div className={css.tileValue}>{results.yoke.val}</div>
                <div className={css.tileMeta}>{results.yoke.note}</div>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className={[css.error, css.errorVisible].join(' ')} role="alert">
            <strong>Outside valid range</strong>
            {error}
          </div>
        )}
      </section>
    </div>
  );
};

export default UPTCalculator;
